import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"

import { emptyBrowseFilter, syncBrowseFilterPlatforms } from "@/utils/browseFilter"
import { deepEqual } from "@/utils/core"
import { applyTalentFilters } from "@/components/talentBrowse/lib/apply"
import { defaultSortForPlatforms, sortOptionsForPlatforms } from "@/components/talentBrowse/lib/sort"
import type { BrowseFilter, CampaignType, Currency, ExchangeRates, List, Platform, Talent } from "@/shared/types"
import { useChunkedWork } from "@/hooks/useChunkedWork"
import { calculateTalentWithPrices } from "@/utils/prices"

const EMPTY_TALENT: Talent[] = []

export type UseTalentBrowseArgs = {
    talent: Talent[]
    lists: List[]
    platforms: Platform[]
    campaignType: CampaignType
    currency: Currency
    exchangeRates: ExchangeRates
    selectedIds: Set<string>
    defaultListId?: string
    defaultSortBy?: string
    defaultFilters?: BrowseFilter
    requirePlatformMembership?: boolean
}

/**
 * Controller for browse choices; called by TalentBrowse, not by page hooks.
 */
export function useTalentBrowse(args: UseTalentBrowseArgs) {
    const [search, setSearch] = useState("")
    const [listId, setListId] = useState(args.defaultListId ?? "")
    const [sortBy, setSortBy] = useState(() => args.defaultSortBy ?? defaultSortForPlatforms(args.platforms))
    const [filters, setFilters] = useState<BrowseFilter>(() => syncBrowseFilterPlatforms(args.defaultFilters ?? emptyBrowseFilter, args.platforms))
    const [filtersOpen, setFiltersOpen] = useState(false)

    const prevDefaultListId = useRef(args.defaultListId)
    useEffect(() => {
        if (args.defaultListId !== prevDefaultListId.current) {
            prevDefaultListId.current = args.defaultListId
            if (args.defaultListId != null) {
                setListId(args.defaultListId)
            }
        }
    }, [args.defaultListId])

    const seededFilters = useRef(args.defaultFilters != null)
    useEffect(() => {
        if (seededFilters.current || args.defaultFilters == null) {
            return
        }
        setFilters(syncBrowseFilterPlatforms(args.defaultFilters, args.platforms))
        seededFilters.current = true
    }, [args.defaultFilters, args.platforms])

    const seededSort = useRef(args.defaultSortBy != null)
    useEffect(() => {
        if (seededSort.current || args.defaultSortBy == null) {
            return
        }
        setSortBy(args.defaultSortBy)
        seededSort.current = true
    }, [args.defaultSortBy])

    const selectedList = args.lists.find((list) => list.id === listId)
    const priceFilter = { currency: args.currency, campaignPlatform: args.platforms[0], campaignType: args.campaignType }
    const listTalent = selectedList?.talent || {}
    // Price in slices so the hamburger still gets turns; keep the last finished array until the new walk ends (All-list keeps prior rows dimmed).
    const { result: pricedTalent, isSlicing } = useChunkedWork(args.talent, {
        createAcc: () => [] as Talent[],
        empty: EMPTY_TALENT,
        processChunk(slice, acc) {
            acc.push(...calculateTalentWithPrices([...slice], args.exchangeRates, priceFilter, listTalent))
        },
        finalize: (acc) => acc,
        workKey: `${args.currency}:${args.platforms[0] ?? ""}:${args.campaignType}:${selectedList?.id ?? ""}:${JSON.stringify(args.exchangeRates)}`,
    })
    const isCustomList = Boolean(selectedList?.uid && selectedList.uid !== "system")
    const sortOptions = sortOptionsForPlatforms(args.platforms, args.campaignType, isCustomList)

    useEffect(() => {
        if (sortOptions.some((option) => option.value === sortBy)) {
            return
        }
        if (args.defaultSortBy && sortBy === args.defaultSortBy) {
            return
        }
        const fallback = defaultSortForPlatforms(args.platforms)
        setSortBy(sortOptions.some((option) => option.value === fallback) ? fallback : sortOptions[0]?.value ?? fallback)
    }, [args.platforms, args.defaultSortBy, sortOptions, sortBy])

    useEffect(() => {
        setFilters((current) => {
            const next = syncBrowseFilterPlatforms(current, args.platforms)
            return deepEqual(next.TikTok, current.TikTok) && deepEqual(next.Instagram, current.Instagram) && deepEqual(next.YouTube, current.YouTube)
                ? current
                : next
        })
    }, [args.platforms])

    const deferredSearch = useDeferredValue(search)
    const deferredFilters = useDeferredValue(filters)
    const deferredListId = useDeferredValue(listId)
    const deferredSortBy = useDeferredValue(sortBy)
    const pending =
        deferredSearch !== search ||
        deferredFilters !== filters ||
        deferredListId !== listId ||
        deferredSortBy !== sortBy ||
        // Same pending dim as deferred filter/search while first-catalog / All-list attach is still slicing.
        isSlicing

    const talent = useMemo(
        () =>
            applyTalentFilters({
                talent: pricedTalent,
                filters: deferredFilters,
                search: deferredSearch,
                listId: deferredListId,
                lists: args.lists,
                platforms: args.requirePlatformMembership === false ? [] : args.platforms,
                campaignType: args.campaignType,
                sortBy: deferredSortBy,
                selectedIds: args.selectedIds,
            }),
        [pricedTalent, deferredFilters, deferredSearch, deferredListId, args.lists, args.platforms, args.requirePlatformMembership, args.campaignType, deferredSortBy, args.selectedIds],
    )

    return {
        search,
        setSearch,
        listId,
        setListId,
        sortBy,
        setSortBy,
        filters,
        setFilters,
        filtersOpen,
        setFiltersOpen,
        sortOptions,
        talent,
        pending,
    }
}
